import React from 'react';
import { Star, Flame, Award, Leaf, Clock } from 'lucide-react';
import { MenuItem } from '../../services/dataService';
import { getCategoryIcon } from './publicMenuCategories';
import { isItemFeatured, isItemPopular } from './publicMenuItemUtils';
import MenuItemCartControls from './MenuItemCartControls';
import { CartItem } from './publicMenuTypes';

type Props = {
  groupedItems: Record<string, MenuItem[]>;
  isTableMode: boolean;
  customerName: string;
  cartItems: CartItem[];
  getItemQuantityInCart: (menuItemId: string) => number;
  onAddToCart: (item: MenuItem) => void;
  onIncrementCart: (cartItemId: string) => void;
  onDecrementCart: (cartItemId: string) => void;
};

const CompactMenuList: React.FC<Props> = ({
  groupedItems,
  isTableMode,
  customerName,
  cartItems,
  getItemQuantityInCart,
  onAddToCart,
  onIncrementCart,
  onDecrementCart,
}) => {
  return (
    <div className="space-y-6">
      {Object.entries(groupedItems).map(([category, items]) => {
        const CategoryIcon = getCategoryIcon(category);
        return (
          <section key={category} className="scroll-mt-36">
            <div className="mb-2 flex items-center gap-2 border-b border-zinc-200 pb-1.5">
              <CategoryIcon className="h-4 w-4 text-amber-600" />
              <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-700">{category}</h2>
            </div>
            <ul className="divide-y divide-zinc-100">
              {items.map((item) => {
                const featured = isItemFeatured(item);
                const popular = isItemPopular(item);
                const qty = item.id ? getItemQuantityInCart(item.id) : 0;

                return (
                  <li key={item.id} className="flex gap-2 py-2.5 first:pt-0">
                    <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-zinc-100">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt=""
                          className="h-full w-full object-cover"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                          }}
                        />
                      ) : null}
                      {item.isTodaysSpecial && (
                        <span className="absolute bottom-0 left-0 right-0 bg-amber-500/90 py-0.5 text-center text-[8px] font-bold text-white">
                          Special
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-sm font-semibold leading-snug text-zinc-900">{item.name}</h3>
                        <div className="shrink-0 text-right">
                          <div className="text-sm font-bold text-amber-600">₹{item.price.toFixed(2)}</div>
                          {item.originalPrice && item.originalPrice > item.price && (
                            <div className="text-[10px] text-zinc-400 line-through">
                              ₹{item.originalPrice.toFixed(2)}
                            </div>
                          )}
                        </div>
                      </div>
                      {item.description && (
                        <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-zinc-500">{item.description}</p>
                      )}
                      <div className="mt-1 flex flex-wrap items-center gap-1">
                        {featured && (
                          <span className="inline-flex items-center gap-0.5 rounded bg-amber-100 px-1 py-0.5 text-[10px] font-medium text-amber-800">
                            <Star className="h-2.5 w-2.5" />
                            Highlight
                          </span>
                        )}
                        {item.isSpecialOffer && (
                          <span className="inline-flex items-center gap-0.5 rounded bg-rose-100 px-1 py-0.5 text-[10px] font-medium text-rose-800">
                            <Flame className="h-2.5 w-2.5" />
                            Offer
                          </span>
                        )}
                        {popular && !featured && (
                          <span className="inline-flex items-center gap-0.5 rounded bg-emerald-100 px-1 py-0.5 text-[10px] font-medium text-emerald-800">
                            <Award className="h-2.5 w-2.5" />
                            Popular
                          </span>
                        )}
                        {item.dietaryInfo?.slice(0, 2).map((info) => (
                          <span
                            key={info}
                            className="inline-flex items-center gap-0.5 rounded bg-green-50 px-1 py-0.5 text-[10px] text-green-800"
                          >
                            <Leaf className="h-2.5 w-2.5" />
                            {info}
                          </span>
                        ))}
                        {item.preparationTime > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-zinc-400">
                            <Clock className="h-2.5 w-2.5" />
                            {item.preparationTime}m
                          </span>
                        )}
                      </div>
                    </div>
                    <MenuItemCartControls
                      item={item}
                      isTableMode={isTableMode}
                      customerName={customerName}
                      quantityInCart={qty}
                      cartItems={cartItems}
                      onAdd={onAddToCart}
                      onIncrement={(id) => onIncrementCart(id)}
                      onDecrement={(id) => onDecrementCart(id)}
                      compact
                    />
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
};

export default CompactMenuList;
